import React from 'react';
import { FaWhatsapp, FaTools, FaLightbulb, FaChartLine } from 'react-icons/fa';

import '../styles.css';
import { useLanguage } from '../contexts/LanguageContext';
import LanguageSwitch from '../components/LanguageSwitch';

type LocalizedText = {
  id: string;
  en: string;
};

type ServiceCategory = {
  title: string;
  intro: LocalizedText;
  items: { name: string; description: LocalizedText }[];
  tagline: LocalizedText;
};

const categories: ServiceCategory[] = [
  {
    title: 'GIS & Mapping',
    intro: {
      id: 'Menghadirkan perspektif geospasial untuk keputusan yang lebih tajam.',
      en: 'Delivering geospatial perspectives for sharper decision making.',
    },
    items: [
      {
        name: 'Peta Tematik',
        description: {
          id: 'Visualisasi data sesuai kebutuhan kebijakan dan kampanye program.',
          en: 'Data visualisation tailored for policy and programme campaigns.',
        },
      },
      {
        name: 'Pemetaan Udara',
        description: {
          id: 'Drone mapping presisi tinggi untuk area perkotaan maupun rural.',
          en: 'High-precision drone mapping for urban and rural areas.',
        },
      },
      {
        name: 'Analisis Spasial',
        description: {
          id: 'Mengubah data geospasial menjadi insight strategis.',
          en: 'Transforming geospatial data into strategic insights.',
        },
      },
    ],
    tagline: {
      id: 'Transformasi geospasial dimulai di sini.',
      en: 'Geospatial transformation starts here.',
    },
  },
  {
    title: 'Digital Product Lab',
    intro: {
      id: 'Membangun solusi digital responsif yang mendukung pertumbuhan bisnis.',
      en: 'Crafting responsive digital solutions that power business growth.',
    },
    items: [
      {
        name: 'Aplikasi Mobile',
        description: {
          id: 'Android/iOS yang stabil, modular, dan mudah dikembangkan ulang.',
          en: 'Stable Android/iOS apps built for modular scalability.',
        },
      },
      {
        name: 'Web App & Landing Page',
        description: {
          id: 'Pengalaman web cepat dengan fokus pada konversi.',
          en: 'Fast web experiences optimised for conversion.',
        },
      },
      {
        name: 'Chatbot & Automation',
        description: {
          id: 'Otomasi percakapan dan alur kerja untuk tim support.',
          en: 'Automated conversations and workflows for support teams.',
        },
      },
    ],
    tagline: {
      id: 'Ayo wujudkan aplikasi impian Anda.',
      en: 'Let’s build the product you imagine.',
    },
  },
  {
    title: 'Story & Campaign Studio',
    intro: {
      id: 'Mengelola konten multi-format untuk komunitas dan brand.',
      en: 'Managing multi-format storytelling for communities and brands.',
    },
    items: [
      {
        name: 'Strategi Konten',
        description: {
          id: 'Rencana konten lintas kanal yang konsisten dan relevan.',
          en: 'Cross-channel content plans that stay consistent and relevant.',
        },
      },
      {
        name: 'Produksi Video & Motion',
        description: {
          id: 'Video kampanye, edukasi, dan highlight acara dengan kualitas sinematik.',
          en: 'Campaign, education, and highlight videos with cinematic polish.',
        },
      },
      {
        name: 'Social Media Management',
        description: {
          id: 'Eksekusi harian hingga laporan performa untuk stakeholder.',
          en: 'Daily execution and performance reporting for stakeholders.',
        },
      },
    ],
    tagline: {
      id: 'Raih perhatian audiens dengan cerita yang menonjol.',
      en: 'Stand out with stories that resonate.',
    },
  },
  {
    title: 'Insight & Data Studio',
    intro: {
      id: 'Mengubah data menjadi narasi yang mudah dipahami tim manajemen.',
      en: 'Turning data into narratives your management teams can act on.',
    },
    items: [
      {
        name: 'Dashboard Interaktif',
        description: {
          id: 'Monitoring real-time untuk KPI yang penting bagi organisasi.',
          en: 'Real-time monitoring for the KPIs that matter to your org.',
        },
      },
      {
        name: 'Visualisasi Story-driven',
        description: {
          id: 'Infografis dan laporan visual untuk publik atau internal.',
          en: 'Infographics and visual reports for public or internal use.',
        },
      },
      {
        name: 'Pelatihan Data Literacy',
        description: {
          id: 'Sesi penguatan kapasitas bagi tim untuk membaca data.',
          en: 'Capacity building sessions to help teams read data.',
        },
      },
    ],
    tagline: {
      id: 'Biarkan data berbicara dengan visual yang kuat.',
      en: 'Let your data speak through powerful visuals.',
    },
  },
];

const highlights = [
  {
    value: '70+',
    label: { id: 'Proyek Digital', en: 'Digital Projects' },
    description: {
      id: 'Solusi web, mobile, dan data yang dirilis dalam dua tahun terakhir.',
      en: 'Web, mobile, and data products launched in the last two years.',
    },
  },
  {
    value: '25',
    label: { id: 'Partner Komunitas', en: 'Community Partners' },
    description: {
      id: 'Kolaborasi dengan organisasi pendidikan dan komunitas lintas kota.',
      en: 'Collaborations with education organisations and communities across cities.',
    },
  },
  {
    value: '9.3/10',
    label: { id: 'Kepuasan Proyek', en: 'Project Satisfaction' },
    description: {
      id: 'Rata-rata umpan balik positif dari klien dan peserta program.',
      en: 'Average positive feedback from clients and programme participants.',
    },
  },
];

const OurHousePage: React.FC = () => {
  const { language } = useLanguage();
  const isEnglish = language === 'en';
  const getText = (text: LocalizedText) => (isEnglish ? text.en : text.id);
  const getSectionBackground = (index: number) =>
    index % 2 === 0 ? 'bg-white' : 'bg-blue-50/60';

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="mx-auto max-w-6xl px-4 py-10">
        <div className="mb-6 flex justify-end">
          <LanguageSwitch />
        </div>

        <section className="relative overflow-hidden rounded-[2.5rem] bg-gradient-to-r from-blue-900 via-blue-800 to-blue-700 p-12 text-white shadow-2xl">
          <div className="absolute inset-y-0 right-0 hidden w-1/2 bg-gradient-to-br from-blue-500/40 via-indigo-400/30 to-sky-300/20 blur-3xl md:block" />
          <div className="relative z-10 max-w-3xl space-y-6 rounded-3xl bg-blue-900/70 p-8 shadow-2xl backdrop-blur">
            <span className="inline-flex items-center gap-2 rounded-full border border-white/30 px-4 py-2 text-xs font-semibold uppercase tracking-[0.2em]">
              {isEnglish ? 'Our House Studio' : 'Studio Our House'}
            </span>
            <h1 className="text-4xl font-black leading-tight text-white md:text-5xl">
              {isEnglish
                ? 'We design, build, and launch digital experiences that move communities forward.'
                : 'Kami merancang, membangun, dan meluncurkan pengalaman digital yang menggerakkan komunitas.'}
            </h1>
            <p className="text-base text-white/90 md:text-lg">
              {isEnglish
                ? 'From geospatial insight to product design, Our House blends strategy, creative thinking, and engineering rigour to deliver measurable change.'
                : 'Mulai dari insight geospasial hingga product design, Our House memadukan strategi, kreativitas, dan engineering untuk menghadirkan perubahan yang terukur.'}
            </p>
            <div className="flex flex-wrap gap-4 text-sm text-blue-100">
              <span className="inline-flex items-center gap-2">
                <FaTools /> {isEnglish ? 'Design-to-delivery in weeks' : 'Desain sampai rilis dalam hitungan minggu'}
              </span>
              <span className="inline-flex items-center gap-2">
                <FaLightbulb /> {isEnglish ? 'Human-centered storytelling' : 'Storytelling berpusat pada manusia'}
              </span>
              <span className="inline-flex items-center gap-2">
                <FaChartLine /> {isEnglish ? 'Impact dashboards ready' : 'Dashboard dampak siap pakai'}
              </span>
            </div>
            <a
              href="https://wa.me/6282151155507"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 rounded-full bg-white px-6 py-3 text-sm font-semibold text-blue-900 shadow-lg transition hover:shadow-xl"
            >
              <FaWhatsapp /> {isEnglish ? 'Plan a discovery call' : 'Jadwalkan sesi discovery'}
            </a>
          </div>
        </section>

        <section className="mt-12 grid gap-6 sm:grid-cols-3">
          {highlights.map((item) => (
            <div key={item.value} className="rounded-3xl border border-blue-100 bg-white p-6 shadow-sm">
              <p className="text-3xl font-black text-blue-800">{item.value}</p>
              <p className="mt-2 text-sm font-semibold text-blue-600">{getText(item.label)}</p>
              <p className="mt-3 text-sm text-gray-600">{getText(item.description)}</p>
            </div>
          ))}
        </section>

        <section className="mt-12 space-y-12">
          {categories.map((category, index) => (
            <div
              key={category.title}
              className={`rounded-[2rem] border border-blue-100 ${getSectionBackground(index)} p-10 shadow-lg transition hover:-translate-y-1 hover:shadow-2xl`}
            >
              <div className="flex flex-col gap-8 md:flex-row md:items-start md:justify-between">
                <div className="max-w-xl space-y-4">
                  <h2 className="text-3xl font-bold text-blue-900 md:text-4xl">{category.title}</h2>
                  <p className="text-sm text-gray-600 md:text-base">{getText(category.intro)}</p>
                </div>
                <div className="grid flex-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {category.items.map((item) => (
                    <div key={item.name} className="rounded-2xl border border-blue-100 bg-white p-5 shadow-sm">
                      <h3 className="text-lg font-semibold text-blue-800">{item.name}</h3>
                      <p className="mt-2 text-sm text-gray-600">{getText(item.description)}</p>
                    </div>
                  ))}
                </div>
              </div>
              <p className="mt-8 text-sm font-semibold text-blue-700 md:text-base">{getText(category.tagline)}</p>
            </div>
          ))}
        </section>

        <section className="mt-16 rounded-[2rem] border border-blue-100 bg-white p-10 text-center shadow-xl">
          <h3 className="text-2xl font-bold text-blue-900 md:text-3xl">
            {isEnglish ? 'Let’s co-create your next digital initiative.' : 'Ayo kembangkan inisiatif digital Anda berikutnya.'}
          </h3>
          <p className="mx-auto mt-3 max-w-2xl text-sm text-gray-600 md:text-base">
            {isEnglish
              ? 'Share your challenge, target audience, and impact goals. Our team will shape a collaboration canvas tailored to your timeline.'
              : 'Bagikan tantangan, audiens, dan target dampak Anda. Tim kami akan menyiapkan canvas kolaborasi yang selaras dengan kebutuhan waktu Anda.'}
          </p>
          <div className="mt-6 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <a
              href="https://wa.me/6282151155507"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 rounded-full bg-blue-600 px-6 py-3 text-sm font-semibold text-white shadow-lg transition hover:bg-blue-700"
            >
              <FaWhatsapp /> {isEnglish ? 'Discuss your idea' : 'Diskusikan ide Anda'}
            </a>
            <a
              href="mailto:business@ourdo.id"
              className="inline-flex items-center gap-2 rounded-full border border-blue-200 px-6 py-3 text-sm font-semibold text-blue-700 transition hover:bg-blue-50"
            >
              business@ourdo.id
            </a>
          </div>
        </section>
      </div>
    </div>
  );
};

export default OurHousePage;
